import { GEO_LOCATION_TYPE } from './geo.constants';
import { STATE_HIGHLIGHT_STYLE } from './map.constants';

export const MAP_ORG_DIVISIONS_ROUTE = '/dashboard/map-org-divisions';

export const MAP_ORG_DIVISIONS_QUERY = {
  PARTY_ID: 'party_id',
  PARTY_NAME: 'party_name',
  LEVEL_NAME: 'level_name',
  LEVEL: 'level',
  FROM: 'from',
};

export function formatMapOrgDivisionsLevelLabel(level) {
  const numeric = Number(level);
  if (!Number.isFinite(numeric) || numeric < 1) {
    return '';
  }
  return `Level ${numeric}`;
}

export function formatMapOrgDivisionsContextTitle(levelName, partyName, level) {
  let title = "Map Org Divisions";

  if (levelName && partyName) {
    title = `Geo mapping for ${levelName} - ${partyName}`;
  } else if (partyName) {
    title = `Geo mapping for ${partyName}`;
  } else if (levelName) {
    title = `Geo mapping for ${levelName}`;
  }

  const levelLabel = formatMapOrgDivisionsLevelLabel(level);
  if (!levelLabel) {
    return title;
  }

  if (title === "Map Org Divisions") {
    return `Geo mapping for ${levelLabel}`;
  }

  return `${title} (${levelLabel})`;
}

export const MAP_ORG_DIVISIONS_GOOGLE_MAP_ID = 'map-org-divisions-google-map';

export const MAP_ORG_DIVISIONS_COORDINATE_PRECISION = 9;

export const MAP_ORG_DIVISIONS_CAMERA = {
  DURATION_MS: 1100,
  MIN_ZOOM: 4,
};

export const MAP_ORG_DIVISIONS_INDIA_FIT_LAYER = {
  code: 'IND',
  locationType: 'COUNTRY',
  geoJson: {
    type: 'Polygon',
    coordinates: [[[68.1, 6.5], [97.4, 6.5], [97.4, 35.7], [68.1, 35.7], [68.1, 6.5]]],
  },
};

export const MAP_ORG_DIVISIONS_DRAW_MODE = {
  PAN: 'select',
  POINT: 'point',
  LINE: 'linestring',
  POLYGON: 'polygon',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  FREEHAND: 'freehand',
};

export const MAP_ORG_DIVISIONS_DRAW_MODE_OPTIONS = [
  { value: MAP_ORG_DIVISIONS_DRAW_MODE.PAN, label: 'Pan' },
  { value: MAP_ORG_DIVISIONS_DRAW_MODE.POINT, label: 'Point' },
  { value: MAP_ORG_DIVISIONS_DRAW_MODE.LINE, label: 'Line' },
  { value: MAP_ORG_DIVISIONS_DRAW_MODE.POLYGON, label: 'Polygon' },
  { value: MAP_ORG_DIVISIONS_DRAW_MODE.RECTANGLE, label: 'Rectangle' },
  { value: MAP_ORG_DIVISIONS_DRAW_MODE.CIRCLE, label: 'Circle' },
  { value: MAP_ORG_DIVISIONS_DRAW_MODE.FREEHAND, label: 'Freehand' },
];

export const MAP_ORG_DIVISIONS_DRAW_TOOLBAR_DEFAULT_POSITION = { x: 16, y: 16 };

export function getMapOrgDivisionsDrawModeLabel(mode) {
  return (
    MAP_ORG_DIVISIONS_DRAW_MODE_OPTIONS.find((option) => option.value === mode)?.label
    || MAP_ORG_DIVISIONS_DRAW_MODE_OPTIONS[0].label
  );
}

export const MAP_ORG_DIVISIONS_DRAW_STYLE = {
  FILL_COLOR: STATE_HIGHLIGHT_STYLE.fillColor,
  FILL_OPACITY: STATE_HIGHLIGHT_STYLE.fillOpacity,
  OUTLINE_COLOR: STATE_HIGHLIGHT_STYLE.color,
  OUTLINE_WIDTH: STATE_HIGHLIGHT_STYLE.weight,
  POINT_COLOR: STATE_HIGHLIGHT_STYLE.color,
};

// Pixel buffer used to snap/close polygon + freehand back onto the start point.
export const MAP_ORG_DIVISIONS_DRAW_POINTER_DISTANCE = 40;

export const MAP_ORG_DIVISIONS_EMPTY_FEATURE_COLLECTION = {
  type: 'FeatureCollection',
  features: [],
};

export function formatMapOrgDivisionsCustomAreaName(index) {
  return `Custom area ${index}`;
}

export function formatMapOrgDivisionsBucketMapLabel(count) {
  return `Assign (${count}) selected areas`;
}

export function formatMapOrgDivisionsDissociateContent(areaName) {
  if (areaName) {
    return `This will unmap ${areaName} from this division. Continue?`;
  }
  return 'This will unmap the selected area from this division. Continue?';
}

export const MAP_ORG_DIVISIONS_MAP_AREA_HINT =
  'Hover a highlighted or drawn area for actions, add it to the list, then Assign. Close polygon/freehand shapes by returning to the start point (or press Enter).';

export const MAP_ORG_DIVISION_ASSIGN_HINT = 'Please select an employee & role type first to assign the OUs.';

export const MAP_ORG_DIVISIONS_AREA_MENU_HOVER_LEAVE_MS = 160;

export const MAP_ORG_DIVISIONS_AREA_MENU_FOLLOW_IDLE_MS = 160;

export const MAP_ORG_DIVISIONS_AREA_MENU_FOLLOW_RESUME_PX = 40;

export const MAP_ORG_DIVISIONS_AREA_MENU_OFFSET = { x: 14, y: 10 };

export const MAP_ORG_DIVISIONS_HOVER_POINTER_DISTANCE = 16;

export const MAP_ORG_DIVISIONS_AREA_MENU_POPUP_CLASS = 'map-org-divisions__area-menu-popup';

export const MAP_ORG_DIVISIONS_AREA_MENU_ANCHOR_CLASS = 'map-org-divisions__area-menu-anchor';

export function formatMapOrgDivisionsAddAreaLabel(areaName) {
  return areaName ? `Add ${areaName}` : "Add this area";
}

export const MAP_ORG_DIVISIONS_BOUNDARY_DASH = '0 8';

export const MAP_ORG_DIVISIONS_BOUNDARY_DASH_PERIOD = 8;

export const MAP_ORG_DIVISIONS_BOUNDARY_DASH_DURATION_MS = 36000;

export const MAP_ORG_DIVISIONS_BOUNDARY_DASH_DISTANCE = 1200;

export const MAP_ORG_DIVISIONS_BOUNDARY_PATH_CLASS = 'map-org-divisions__boundary-path';

export const MAP_ORG_DIVISIONS_HOVER_PATH_CLASS = 'map-org-divisions__boundary-path--hover';

export const MAP_ORG_DIVISIONS_BOUNDARY_LINE_CAP = 'round';

export const MAP_ORG_DIVISIONS_HOVER_HIGHLIGHT = {
  WEIGHT: 1,
  SHADOW_WEIGHT: 2,
  SHADOW_OPACITY: 0.2,
  Z_INDEX: 80,
};

export const MAP_ORG_DIVISIONS_BOUNDARY_DOT_WEIGHT = 1.5;

export const MAP_ORG_DIVISIONS_BOUNDARY_DASH_ICON = {
  SCALE: 1.25,
  STROKE_WEIGHT: 0,
  FILL_OPACITY: 1,
  REPEAT: '8px',
};

export const MAP_ORG_DIVISIONS_BOUNDARY_STYLE = {
  [GEO_LOCATION_TYPE.STATE]: {
    strokeColor: '#0c4e8d',
    fillColor: '#0c4e8d',
    fillOpacity: 0.22,
    weight: 3,
    zIndex: 10,
  },
  [GEO_LOCATION_TYPE.DISTRICT]: {
    strokeColor: '#1278fc',
    fillColor: '#1278fc',
    fillOpacity: 0.28,
    weight: 3,
    zIndex: 20,
  },
  [GEO_LOCATION_TYPE.SUB_DISTRICT]: {
    strokeColor: '#e57700',
    fillColor: '#e57700',
    fillOpacity: 0.34,
    weight: 3,
    zIndex: 30,
  },
  [GEO_LOCATION_TYPE.BLOCK]: {
    strokeColor: '#00897b',
    fillColor: '#00897b',
    fillOpacity: 0.34,
    weight: 3,
    zIndex: 35,
  },
  [GEO_LOCATION_TYPE.VILLAGE]: {
    strokeColor: '#d42215',
    fillColor: '#d42215',
    fillOpacity: 0.4,
    weight: 3,
    zIndex: 40,
  },
  [GEO_LOCATION_TYPE.CUSTOM]: {
    strokeColor: '#6a1b9a',
    fillColor: '#6a1b9a',
    fillOpacity: 0.32,
    weight: 3,
    zIndex: 50,
  },
};

/** Distinct palette for highlighted / selected geo areas on the map. */
export const MAP_ORG_DIVISIONS_AREA_COLORS = [
  '#0c4e8d',
  '#1278fc',
  '#e57700',
  '#d42215',
  '#0f9d58',
  '#8e24aa',
  '#00897b',
  '#c2185b',
  '#5c6bc0',
  '#ef6c00',
  '#00838f',
  '#6a1b9a',
  '#3949ab',
  '#c62828',
  '#2e7d32',
  '#6d4c41',
];

export function getMapOrgDivisionsBoundaryStyle(locationType) {
  return (
    MAP_ORG_DIVISIONS_BOUNDARY_STYLE[locationType] ||
    MAP_ORG_DIVISIONS_BOUNDARY_STYLE[GEO_LOCATION_TYPE.DISTRICT]
  );
}

/** Stable pseudo-random color from the palette for a geo area code. */
export function getMapOrgDivisionsAreaColor(code) {
  const key = String(code || '');
  if (!key || !MAP_ORG_DIVISIONS_AREA_COLORS.length) {
    return MAP_ORG_DIVISIONS_BOUNDARY_STYLE[GEO_LOCATION_TYPE.DISTRICT].strokeColor;
  }

  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }

  return MAP_ORG_DIVISIONS_AREA_COLORS[hash % MAP_ORG_DIVISIONS_AREA_COLORS.length];
}

export function resolveMapOrgDivisionsLayerStyle(layer) {
  const base = getMapOrgDivisionsBoundaryStyle(layer?.locationType);
  const color = layer?.color || getMapOrgDivisionsAreaColor(layer?.code);

  return {
    ...base,
    strokeColor: color,
    fillColor: color,
  };
}

export function resolveMapOrgDivisionsHoverLayerStyle(layer) {
  const style = resolveMapOrgDivisionsLayerStyle(layer);
  if (!style) {
    return null;
  }

  return {
    ...style,
    weight: MAP_ORG_DIVISIONS_HOVER_HIGHLIGHT.WEIGHT,
    fillOpacity: 0,
    zIndex: MAP_ORG_DIVISIONS_HOVER_HIGHLIGHT.Z_INDEX,
  };
}
